import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MdClose, MdDownload, MdPayments, MdWarning } from 'react-icons/md'
import { toast } from 'react-toastify'
import {
    PayoutBatchFilters,
    PayoutBatchGroup,
    PayoutBatchItem,
    PayoutBatchResult,
    PayoutBatchSkipped,
    generatePayoutBatch,
    previewPayoutBatch,
} from '@/lib/api/financial/payout-batch-api'
import { formatCurrency } from '../config/report-configs'
import PayoutBatchItemsPicker from './payout-batch-items-picker'

interface PayoutBatchModalProps {
    isOpen: boolean
    onClose: () => void
    filters: PayoutBatchFilters
    /**
     * O relatório estava contando o período por uma base que o agrupamento não tem
     * (`paid`), e o filtro foi convertido para data de criação. Sem o aviso, o
     * operador compara duas listas de períodos diferentes sem saber.
     */
    periodBasisChanged?: boolean
    /** Recarrega a listagem: os repasses agrupados saem do filtro "sem lote". */
    onGenerated?: () => void
}

/**
 * Gera a solicitação agrupada de repasse a partir do filtro corrente da aba
 * Lançamentos.
 *
 * Dois passos, de propósito: primeiro o operador **confere** o que o filtro pegou,
 * por destinatário, e só então gera. Um agrupamento cria conta a pagar de verdade —
 * não é uma exportação que se refaz sem custo.
 *
 * **Um destinatário por vez.** Cada parceiro tem a sua data de fechamento e a sua
 * forma de pagamento combinada, e o financeiro gera na hora de pagar aquele. Gerar
 * cinco de uma vez também impediria o passo seguinte: a solicitação nasce fora da
 * fila e ele vai direto configurar vencimento e formas — o que só existe para uma.
 */
export default function PayoutBatchModal({
    isOpen,
    onClose,
    filters,
    periodBasisChanged,
    onGenerated,
}: PayoutBatchModalProps) {
    const [loading, setLoading] = useState(false)
    const [generating, setGenerating] = useState(false)
    const [groups, setGroups] = useState<PayoutBatchGroup[]>([])
    const [selectedKey, setSelectedKey] = useState<string | null>(null)
    const [expanded, setExpanded] = useState<Set<string>>(new Set())
    /**
     * O sentido da seleção no "Detalhar".
     *
     * `exclude` (padrão): entra tudo, o operador desmarca as exceções. `include` (o
     * botão "Inverter"): não entra nada até ele marcar — é o caso de pagar só um
     * punhado de uma lista longa, onde desmarcar dezenas é justamente onde se erra.
     */
    const [selectionMode, setSelectionMode] = useState<'exclude' | 'include'>(
        'exclude'
    )
    /**
     * Os lançamentos marcados no "Detalhar", com o valor de cada um — lidos conforme
     * o `selectionMode` (os que saem, ou os únicos que entram).
     *
     * Vale só para esta geração: nada é marcado no lançamento, ele continua em aberto
     * e volta no próximo preview. O agrupamento **automático** (o ciclo do cadastro do
     * destinatário) também o inclui — automação pega tudo, e o caso de uso aqui é
     * "hoje não, amanhã pode".
     */
    const [marked, setMarked] = useState<Map<number, number>>(new Map())
    const [result, setResult] = useState<PayoutBatchResult | null>(null)
    /**
     * Por que os lançamentos que a tela lista ficaram de fora.
     *
     * A aba Lançamentos mostra toda conta a pagar do período; o agrupamento só leva
     * repasse em aberto, sem lote e com destinatário. Sem isto, a diferença aparecia
     * como um "nenhum repasse em aberto" com dois lançamentos visíveis atrás do modal.
     */
    const [skipped, setSkipped] = useState<PayoutBatchSkipped | null>(null)
    const navigate = useNavigate()

    const keyOf = (g: PayoutBatchGroup) => `${g.kind}:${g.entityId}`

    /** Quantos lançamentos do filtro ficaram de fora, por qualquer motivo. */
    const skippedCount = skipped
        ? skipped.notPayoutCategory +
          skipped.alreadyBatched +
          skipped.notOpen +
          skipped.withoutRecipient
        : 0

    useEffect(() => {
        if (!isOpen) return
        setResult(null)
        setMarked(new Map())
        setSelectionMode('exclude')
        setLoading(true)
        previewPayoutBatch(filters)
            .then(res => {
                const list = res.result?.groups ?? []
                setGroups(list)
                setSkipped(res.result?.skipped ?? null)
                // Um só na lista: já vem marcado. Vários: nenhum — a escolha é
                // deliberada, e marcar um por conta própria arriscaria gerar a conta
                // do parceiro errado.
                setSelectedKey(list.length === 1 ? keyOf(list[0]) : null)
            })
            .catch(() => toast.error('Erro ao carregar os repasses do período'))
            .finally(() => setLoading(false))
    }, [isOpen, JSON.stringify(filters)])

    /**
     * Trocar de destinatário zera a seleção — mas só quando havia outro antes.
     *
     * Ela é desta geração, e a geração é de um destinatário só: mantida na troca, o
     * rodapé do parceiro B contaria linhas que só existem no A. Já o **primeiro**
     * clique no rádio não pode limpar nada: o operador que abre "Detalhar", desmarca o
     * que não vai pagar e só então escolhe o destinatário perderia o trabalho inteiro
     * sem ver por quê.
     */
    const previousKey = useRef<string | null>(null)
    useEffect(() => {
        if (previousKey.current && previousKey.current !== selectedKey) {
            setMarked(new Map())
            setSelectionMode('exclude')
        }
        previousKey.current = selectedKey
    }, [selectedKey])

    const toggle = (
        key: string,
        set: Set<string>,
        apply: (s: Set<string>) => void
    ) => {
        const next = new Set(set)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        apply(next)
    }

    /**
     * Marca/desmarca um lançamento, guardando o **valor** junto.
     *
     * Sem o valor não daria para acertar o rodapé: o total do destinatário vem
     * agregado do banco e a tela só conhece a página aberta — a linha marcada na
     * página 3 pode nem estar mais montada quando o rodapé recalcula.
     */
    const toggleItem = (item: PayoutBatchItem) => {
        const next = new Map(marked)
        if (next.has(item.billingId)) next.delete(item.billingId)
        else next.set(item.billingId, item.amount)
        setMarked(next)
    }

    /**
     * Inverter troca o sentido e **zera** as marcações.
     *
     * Não dá para converter uma seleção na outra: a tela conhece só a página aberta, e
     * "todos menos estes cinco" viraria uma lista de ids que ela não tem. Zerar deixa o
     * estado óbvio — no modo invertido, nada entra até ser marcado.
     */
    const invertSelection = () => {
        setSelectionMode(m => (m === 'exclude' ? 'include' : 'exclude'))
        setMarked(new Map())
    }

    const selectedGroup = groups.find(g => keyOf(g) === selectedKey) ?? null
    /**
     * O rodapé conta e soma o que **vai** para a solicitação — com um lançamento fora,
     * o número do grupo deixa de ser o valor da conta que será criada.
     *
     * No modo padrão os totais do grupo vêm agregados do banco e daqui sai só o
     * desconto do que foi desmarcado. No invertido é o oposto: a soma é só do que foi
     * marcado, e não há agregado nenhum a descontar.
     */
    const markedIds = Array.from(marked.keys())
    const markedCount = markedIds.length
    const markedAmount = Array.from(marked.values()).reduce(
        (sum, amount) => sum + amount,
        0
    )
    const isInverted = selectionMode === 'include'
    const selectedCount = isInverted
        ? markedCount
        : Math.max(0, (selectedGroup?.count ?? 0) - markedCount)
    const selectedTotal = isInverted
        ? markedAmount
        : Math.max(0, (selectedGroup?.totalAmount ?? 0) - markedAmount)

    const handleGenerate = async () => {
        if (!selectedGroup || selectedCount === 0) return
        setGenerating(true)
        try {
            const res = await generatePayoutBatch(
                filters,
                [{ kind: selectedGroup.kind, entityId: selectedGroup.entityId }],
                { mode: selectionMode, ids: markedIds }
            )
            const data = res.result
            setResult(data)
            const batch = data?.created?.[0]
            if (batch) {
                onGenerated?.()
                // A solicitação nasce FORA da fila: o financeiro define vencimento e
                // formas de pagamento antes de mandar aprovar. Levar direto para a
                // edição é o fluxo — deixá-la parada numa lista seria contar com
                // alguém lembrar de voltar.
                toast.success(
                    'Conta a pagar criada. Confira o vencimento e as formas de pagamento antes de enviar para aprovação.'
                )
                onClose()
                navigate(`/financial/billings/${batch.batchId}/edit`)
                return
            }
            if (data?.skipped?.length) {
                toast.warning(
                    `${data.skipped.length} destinatário(s) não puderam ser agrupados`
                )
            }
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ||
                    'Erro ao gerar a solicitação agrupada'
            )
        } finally {
            setGenerating(false)
        }
    }

    if (!isOpen) return null

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-4xl">
                <div className="flex items-center justify-between gap-3 mb-1">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <MdPayments className="text-primary" size={22} />
                        Gerar conta a pagar agrupada
                    </h3>
                    <button
                        className="btn btn-sm btn-circle btn-ghost"
                        onClick={onClose}
                        aria-label="Fechar"
                    >
                        <MdClose size={18} />
                    </button>
                </div>

                <p className="text-sm text-base-content/70 mb-4">
                    Escolha o destinatário: a solicitação soma os repasses em
                    aberto dele no filtro. Em <strong>Detalhar</strong> você pode
                    desmarcar um lançamento que ainda não deve ser pago — ou usar{' '}
                    <strong>Inverter seleção</strong> quando for mais rápido
                    marcar só os poucos que entram. O que ficar de fora continua
                    em aberto e volta no próximo agrupamento. Ela nasce fora da
                    fila para você definir vencimento e formas de pagamento — só
                    então vai para aprovação. Ao ser paga, todos os lançamentos
                    dela recebem baixa automaticamente.
                </p>

                {periodBasisChanged ? (
                    <div className="alert alert-info py-2 mb-3 text-sm">
                        <MdWarning size={18} />
                        <span>
                            O relatório está contando o período pela{' '}
                            <strong>data de pagamento</strong>. O agrupamento usa a
                            data de criação do lançamento — e repasse já pago não é
                            agrupável.
                        </span>
                    </div>
                ) : null}

                {loading ? (
                    <div className="flex justify-center py-10">
                        <span className="loading loading-spinner loading-lg" />
                    </div>
                ) : result ? (
                    <ResultView result={result} />
                ) : groups.length === 0 ? (
                    <EmptyView skipped={skipped} />
                ) : (
                    <>
                        {/* Parte do que a listagem mostra não é agrupável, e o
                            operador precisa saber disso antes de estranhar o total. */}
                        {skippedCount > 0 ? (
                            <p className="text-xs text-base-content/60 mb-2">
                                {skippedCount} dos {skipped!.total} lançamentos a
                                pagar do filtro não entram no agrupamento (já
                                agrupados, pagos, ou que não são repasse).
                            </p>
                        ) : null}
                        <div className="max-h-[45vh] overflow-y-auto flex flex-col gap-2">
                            {groups.map(group => {
                                const key = keyOf(group)
                                const isOpenRow = expanded.has(key)
                                return (
                                    <div
                                        key={key}
                                        className="border border-base-300 rounded-lg"
                                    >
                                        <div className="flex items-center gap-3 p-3">
                                            <input
                                                type="radio"
                                                name="payout-batch-recipient"
                                                className="radio radio-sm"
                                                checked={selectedKey === key}
                                                onChange={() =>
                                                    setSelectedKey(key)
                                                }
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="font-medium truncate">
                                                    {group.name}
                                                </div>
                                                <div className="text-xs text-base-content/60 flex items-center gap-1.5 flex-wrap">
                                                    <span>
                                                        {group.kindLabel} ·{' '}
                                                        {group.count}{' '}
                                                        atendimento
                                                        {group.count > 1
                                                            ? 's'
                                                            : ''}
                                                    </span>
                                                    {/* Ciclo do cadastro: é ele que liga
                                                        o agrupamento automático. Sem data
                                                        salva, este destinatário só é
                                                        agrupado à mão. */}
                                                    {group.schedule
                                                        ?.fixedDay ? (
                                                        <span className="badge badge-xs bg-secondary/15 text-secondary border-0">
                                                            Fecha todo dia{' '}
                                                            {
                                                                group.schedule
                                                                    .fixedDay
                                                            }
                                                        </span>
                                                    ) : (
                                                        <span
                                                            className="badge badge-xs badge-ghost"
                                                            title="Defina o dia de fechamento no cadastro do destinatário para o agrupamento passar a ser automático"
                                                        >
                                                            Sem ciclo definido
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-semibold">
                                                    {formatCurrency(
                                                        group.totalAmount
                                                    )}
                                                </div>
                                                <button
                                                    className="btn btn-ghost btn-xs"
                                                    onClick={() =>
                                                        toggle(
                                                            key,
                                                            expanded,
                                                            setExpanded
                                                        )
                                                    }
                                                >
                                                    {isOpenRow
                                                        ? 'Ocultar'
                                                        : 'Detalhar'}
                                                </button>
                                            </div>
                                        </div>

                                        {isOpenRow ? (
                                            <PayoutBatchItemsPicker
                                                filters={filters}
                                                recipient={{
                                                    kind: group.kind,
                                                    entityId: group.entityId,
                                                }}
                                                mode={selectionMode}
                                                marked={marked}
                                                onToggle={toggleItem}
                                                onInvert={invertSelection}
                                            />
                                        ) : null}
                                    </div>
                                )
                            })}
                        </div>

                        <div className="flex items-center justify-between gap-3 mt-4 p-3 bg-base-200 rounded-lg">
                            <span className="text-sm">
                                {selectedGroup ? (
                                    <>
                                        {selectedGroup.name} · {selectedCount}{' '}
                                        lançamento
                                        {selectedCount === 1 ? '' : 's'}
                                        {isInverted ? (
                                            <span
                                                className="text-xs text-secondary ml-1"
                                                title="Seleção invertida: só o que você marcar entra. O resto continua em aberto e volta no próximo agrupamento"
                                            >
                                                (seleção invertida — só os
                                                marcados entram)
                                            </span>
                                        ) : markedCount > 0 ? (
                                            <span
                                                className="text-xs text-warning ml-1"
                                                title="Eles continuam em aberto e voltam no próximo agrupamento"
                                            >
                                                ({markedCount} fora desta
                                                solicitação)
                                            </span>
                                        ) : null}
                                    </>
                                ) : (
                                    'Escolha um destinatário'
                                )}
                            </span>
                            <span className="font-bold text-lg">
                                {formatCurrency(selectedTotal)}
                            </span>
                        </div>
                    </>
                )}

                <div className="modal-action">
                    <button className="btn btn-ghost" onClick={onClose}>
                        {result ? 'Fechar' : 'Cancelar'}
                    </button>
                    {!result ? (
                        <button
                            className="btn btn-primary"
                            disabled={
                                loading ||
                                generating ||
                                !selectedGroup ||
                                selectedCount === 0
                            }
                            onClick={handleGenerate}
                        >
                            {generating ? (
                                <>
                                    <span className="loading loading-spinner loading-sm" />
                                    Gerando...
                                </>
                            ) : (
                                'Gerar solicitação'
                            )}
                        </button>
                    ) : null}
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </div>
    )
}

/**
 * Os motivos de descarte, em texto — na ordem em que o backend os apura.
 *
 * Cada frase termina o começo "Dos N lançamentos a pagar do filtro, ...", por isso
 * são orações e não rótulos soltos.
 */
const SKIP_REASONS: Array<{
    key: keyof PayoutBatchSkipped
    label: (count: number) => string
}> = [
    {
        key: 'notPayoutCategory',
        label: c =>
            `${c} não ${c > 1 ? 'são repasses' : 'é repasse'} (assinatura, despesa avulsa, ` +
            'uma solicitação agrupada, ou lançamento antigo sem categoria)',
    },
    {
        key: 'alreadyBatched',
        label: c => `${c} já ${c > 1 ? 'estão' : 'está'} em uma solicitação agrupada`,
    },
    {
        key: 'notOpen',
        label: c =>
            `${c} não ${c > 1 ? 'estão' : 'está'} em aberto (pago, cancelado ou estornado)`,
    },
    {
        key: 'withoutRecipient',
        label: c => `${c} ${c > 1 ? 'estão' : 'está'} sem destinatário preenchido`,
    },
]

/**
 * Por que o filtro que lista lançamentos na tela não rendeu nenhum agrupamento.
 *
 * Antes esta tela dizia apenas "nenhum repasse em aberto no filtro atual", e o
 * operador ficava com a listagem cheia atrás do modal e sem o que fazer. Os motivos
 * vêm do servidor, contados sobre o mesmo filtro.
 */
function EmptyView({ skipped }: { skipped: PayoutBatchSkipped | null }) {
    const reasons = skipped
        ? SKIP_REASONS.filter(r => (skipped[r.key] as number) > 0).map(r =>
              r.label(skipped[r.key] as number)
          )
        : []

    return (
        <div className="alert alert-warning items-start">
            <MdWarning size={20} className="mt-0.5" />
            <div className="flex flex-col gap-1 text-sm">
                <span className="font-medium">
                    Nenhum repasse agrupável no filtro atual.
                </span>
                {reasons.length > 0 ? (
                    <>
                        <span>
                            Dos {skipped!.total} lançamentos a pagar do período:
                        </span>
                        <ul className="list-disc list-inside">
                            {reasons.map(text => (
                                <li key={text}>{text}</li>
                            ))}
                        </ul>
                    </>
                ) : (
                    <span>
                        Nenhuma conta a pagar no período. Ajuste o período ou o
                        destinatário e tente de novo.
                    </span>
                )}
            </div>
        </div>
    )
}

function ResultView({ result }: { result: PayoutBatchResult }) {
    return (
        <div className="flex flex-col gap-3">
            {result.created.length > 0 ? (
                <div className="overflow-x-auto">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th>Solicitação</th>
                                <th>Destinatário</th>
                                <th className="text-right">Valor</th>
                                <th>Demonstrativo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {result.created.map(c => (
                                <tr key={c.batchId}>
                                    <td>
                                        <a
                                            className="link link-primary"
                                            href={`/financial/billings/${c.batchId}`}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            #{c.batchId}
                                        </a>
                                    </td>
                                    <td>
                                        {c.recipientName}
                                        <span className="text-xs text-base-content/60 block">
                                            {c.itemCount} lançamento
                                            {c.itemCount > 1 ? 's' : ''}
                                        </span>
                                    </td>
                                    <td className="text-right font-medium">
                                        {formatCurrency(c.amount)}
                                    </td>
                                    <td>
                                        {c.pdfUrl ? (
                                            <a
                                                className="btn btn-ghost btn-xs"
                                                href={c.pdfUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <MdDownload size={16} />
                                                PDF
                                            </a>
                                        ) : (
                                            <span
                                                className="text-xs text-base-content/60"
                                                title="A solicitação foi criada e segue pagável; o documento pode ser gerado de novo"
                                            >
                                                Falhou
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}

            {result.skipped.length > 0 ? (
                <div className="alert alert-warning">
                    <MdWarning size={20} />
                    <div>
                        <div className="font-medium">
                            Não agrupados ({result.skipped.length})
                        </div>
                        <ul className="text-sm list-disc list-inside">
                            {result.skipped.map(s => (
                                <li key={s.recipientName}>
                                    {s.recipientName}: {s.reason}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            ) : null}

            <div className="alert alert-info text-sm">
                <span>
                    As solicitações estão aguardando aprovação em{' '}
                    <strong>Financeiro → Aprovações</strong>. Ao serem pagas, os
                    lançamentos que elas somam recebem baixa automaticamente.
                </span>
            </div>
        </div>
    )
}
